/**
 * Grants or revokes the browser-side ad tracking consent on every pixel that may be loaded:
 * Meta (fbq), TikTok (ttq), ChatGPT Ads (oaiq), Snapchat (snaptr), Pinterest (pintrk), Microsoft/Bing UET (uetq) and Google via the GTM
 * data layer (the cookie-consent events drive Google Consent Mode in the GTM container). Each call
 * is wrapped separately: a pixel is only present when the visitor came through that platform's ad.
 *
 * Bing: main_header.html.twig loads the UET tag with `consent default ad_storage=denied` until the banner
 * is accepted; this `update` switches it on or off. https://help.ads.microsoft.com/#apex/ads/en/60119/1
 */
const setBingConsent = (adStorage: "granted" | "denied") => {
  try {
    const uetq: UetQueue = window.uetq ?? ([] as unknown[]);
    window.uetq = uetq;
    uetq.push("consent", "update", { ad_storage: adStorage });
  } catch (e) {
    console.log(e);
  }
};

/**
 * Snapchat: the Snap Pixel has no consent API, so main_header.html.twig only queues its calls and leaves
 * loading scevent.min.js to `snaptr.load()`. There is nothing to revoke: without consent it never loaded.
 */
const loadSnapPixel = () => {
  try {
    window.snaptr?.load?.();
  } catch (e) {
    console.log(e);
  }
};

/**
 * Pinterest: `pintrk('setconsent', false)` drops events rather than holding them, so main_header.html.twig only
 * queues the calls and leaves loading core.js to `pintrk.loadScript()`, which replays the queue.
 * https://help.pinterest.com/en/business/article/install-the-base-code
 */
const loadPinterestTag = () => {
  try {
    window.pintrk?.loadScript?.();
  } catch (e) {
    console.log(e);
  }
};

export const grantAdConsentInBrowser = () => {
  try {
    fbq("consent", "grant");
  } catch (e) {
    console.log(e);
  }

  try {
    ttq.grantConsent();
  } catch (e) {
    console.log(e);
  }

  try {
    oaiq("consent", true);
  } catch (e) {
    console.log(e);
  }

  loadSnapPixel();

  loadPinterestTag();

  setBingConsent("granted");

  try {
    window.dataLayer = window.dataLayer || [];
    /**
     * This is a custom event that was set up through tagmanager.google.com
     * https://tagmanager.google.com/#/container/accounts/6006110073/containers/60006331/workspaces/33/triggers
     */
    window.dataLayer.push({ event: "cookie-consent" });
  } catch (e) {
    console.log(e);
  }
};

export const revokeAdConsentInBrowser = () => {
  try {
    fbq("consent", "revoke");
  } catch (e) {
    console.log(e);
  }

  try {
    ttq.revokeConsent();
  } catch (e) {
    console.log(e);
  }

  try {
    oaiq("consent", false);
  } catch (e) {
    console.log(e);
  }

  try {
    // Stops sending and deletes the tag's first-party cookies, should core.js have been loaded
    window.pintrk?.("setconsent", false);
  } catch (e) {
    console.log(e);
  }

  setBingConsent("denied");

  try {
    window.dataLayer = window.dataLayer || [];
    /**
     * This is a custom event that was set up through tagmanager.google.com
     * https://tagmanager.google.com/#/container/accounts/6006110073/containers/60006331/workspaces/33/triggers
     */
    window.dataLayer.push({ event: "cookie-consent-decline" });
  } catch (e) {
    console.log(e);
  }
};
